/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import type { TFunction } from 'i18next';
import type { VisitKanbanStatus } from './useVisitsKanban';

export const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00 – 19:00

/**
 * Returns the localized short weekday labels (Sun–Sat) for the calendar header.
 *
 * @param t - i18next translation function.
 * @returns The seven weekday labels starting on Sunday.
 */
export function getDayLabels(t: TFunction): string[] {
  return [
    t('visits.calendar.days.sun'),
    t('visits.calendar.days.mon'),
    t('visits.calendar.days.tue'),
    t('visits.calendar.days.wed'),
    t('visits.calendar.days.thu'),
    t('visits.calendar.days.fri'),
    t('visits.calendar.days.sat'),
  ];
}

export const STATUS_COLORS: Record<VisitKanbanStatus, { bg: string; text: string; border: string }> = {
  pending:   { bg: 'var(--status-warning-bg)',  text: 'var(--status-warning-text)',  border: '#eab308' },
  confirmed: { bg: 'var(--status-success-bg)',  text: 'var(--status-success-text)',  border: '#22c55e' },
  done:      { bg: 'var(--surface-tertiary)',   text: 'var(--text-tertiary)',         border: '#3b82f6' },
  cancelled: { bg: 'var(--status-error-bg)',    text: 'var(--status-error-text)',     border: '#ef4444' },
};

/**
 * Builds the seven consecutive dates of a week starting at the given date.
 *
 * @param startDate - Week start date (`YYYY-MM-DD`).
 * @returns An array of seven {@link Date} objects for the week.
 */
export function getWeekDays(startDate: string): Date[] {
  const [y, m, d] = startDate.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(base);
    day.setDate(base.getDate() + i);
    return day;
  });
}

/**
 * Formats a {@link Date} as a zero-padded `YYYY-MM-DD` string in local time.
 *
 * @param d - Date to format.
 * @returns The `YYYY-MM-DD` representation.
 */
export function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Extracts the hour component from an `HH:MM` time string, defaulting to 9.
 *
 * @param time - Time string (`HH:MM`).
 * @returns The hour as an integer.
 */
export function visitHour(time: string): number {
  return parseInt(time.split(':')[0] ?? '9', 10);
}
